import { chartsComputing, generateColumnColor } from "$root/functional-areas/portfolio-lab/common";
import { PortfolioLabContext } from "$root/functional-areas/portfolio-lab/ctx";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	PortfolioQueryWidgetBase,
	type PortfolioWidgetData,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedDynamicQueries } from "$root/third-party-integrations/react-query/hooks";
import type { DataResponseDTO, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import { Banner, Column } from "@mdotm/shadcn/legacy/components";
import { useDeepEqualMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { clamp } from "@mdotm/shadcn/legacy/utils";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { portfolioLabRepository } from "../../../repository";
import { buildConceptMetricColumnDefs, type ConceptMetricsRow, extractConceptMetricValues } from "../concept-metrics";
import { LabCalculatingWall } from "../LabCalculatingWall";

const EMPTY_CONCEPTS: ParameterizedConceptDTO[] = [];

/**
 * Metrics-only body of the Performance chart when a forward-looking scenario is selected: no
 * performance series exists, so it renders the ex-ante concepts (return / volatility) fetched
 * through the data provider, one row per lab investment. Columns derive from the parametrized
 * concepts + concept definitions, like PortfolioMetrics. Mounted ONLY while a forward-looking
 * scenario is selected — the historical series queries live in the sibling branch, so each
 * branch's queries exist exactly when that branch is active.
 */
export function ExAnteScenarioMetrics({
	scenarioId,
	showDelta,
}: {
	scenarioId: string;
	/** Per-chart "Show delta" preference — gates both the baseline queries and the delta rendering. */
	showDelta: boolean;
}) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investments = useAtomValue(ctx.investments$);
	const metricsStatus = useAtomValue(ctx.metricsStatus$);
	const { formatNumber } = useLocaleFormatters();

	// Session-pinned (staleTime/cacheTime Infinity): resolved once, uuids stay stable across scenario changes.
	const conceptsConfigQuery = portfolioLabRepository.exAnteScenarioConceptsConfig.useQuery();
	const parametrizedConcepts = conceptsConfigQuery.data?.parametrizedConcepts ?? EMPTY_CONCEPTS;

	const metricQueries = useDeepEqualMemo(
		() =>
			investments.map((inv) => ({
				...portfolioLabRepository.getExAnteScenarioMetrics.getQueryConfig(
					inv.virtualUuid,
					scenarioId,
					parametrizedConcepts,
				),
				enabled: parametrizedConcepts.length > 0,
				keepPreviousData: true,
			})),
		[investments, scenarioId, parametrizedConcepts],
	);
	const metricsQueryRaw = useQueries({ queries: metricQueries });

	// Baseline (pre-edit) metrics power the in-cell deltas. Deliberately NOT merged into the mapped
	// widget query: a missing/failed baseline must never block or error the grid — cells just render
	// without the delta.
	const baselineQueries = useDeepEqualMemo(
		() =>
			investments.map((inv) => ({
				...portfolioLabRepository.getExAnteScenarioMetricsBaseline.getQueryConfig(
					inv.virtualUuid,
					scenarioId,
					parametrizedConcepts,
				),
				// NOT gated on `showDelta`: with staleTime 0, re-enabling would refetch and can
				// replace the on-screen baseline mid-session — the toggle is purely presentational.
				enabled: parametrizedConcepts.length > 0,
				keepPreviousData: true,
			})),
		[investments, scenarioId, parametrizedConcepts],
	);
	const baselineMetricsQueryRaw = useQueries({ queries: baselineQueries });
	const baselineMetricValues = baselineMetricsQueryRaw.map((result) =>
		result.data != null ? extractConceptMetricValues(result.data, parametrizedConcepts) : undefined,
	);

	const rowsQuery = useMappedDynamicQueries(
		(results: DataResponseDTO[]): PortfolioWidgetData<ConceptMetricsRow[]> => {
			if (metricsStatus === "calculating" || investments.some(chartsComputing)) {
				return { widgetStatus: WidgetStatus.CALCULATING, data: [] };
			}
			const rows = investments.map((inv, i) => ({
				// Keyed by virtualUuid, not the source uuid: the same investment can be loaded twice
				// (two lab columns, same source), and source-uuid keys would collapse them into one row.
				uuid: inv.virtualUuid,
				name: inv.name,
				color: generateColumnColor(i),
				metricValues: extractConceptMetricValues(results[i], parametrizedConcepts),
				baselineMetricValues: baselineMetricValues[i],
			}));
			if (rows.length === 0) {
				return { widgetStatus: WidgetStatus.EMPTY, data: [] };
			}
			return { widgetStatus: WidgetStatus.READY, data: rows };
		},
		[investments, parametrizedConcepts, metricsStatus, baselineMetricValues],
		metricsQueryRaw,
	);

	const conceptDefsById = useMemo(
		() => new Map((conceptsConfigQuery.data?.conceptDefinitions ?? []).map((d) => [d.id!, d])),
		[conceptsConfigQuery.data],
	);
	const columnDefs = useDeepEqualMemo(
		() =>
			buildConceptMetricColumnDefs({
				orderedMetricUuids: parametrizedConcepts.map((c) => c.uuid!).filter(Boolean),
				parametrizedConcepts,
				conceptDefsById,
				formatNumber,
				showDelta,
				valueQualifier: "PortfolioLab/Forecast/Value",
			}),
		[parametrizedConcepts, conceptDefsById, formatNumber, showDelta],
	);

	return (
		<PortfolioQueryWidgetBase
			query={rowsQuery}
			iconWalls={{
				calculating: <LabCalculatingWall />,
			}}
		>
			{(rows) => (
				<Column flexGrow={0} gap={0}>
					<Banner severity="info" className="mb-2">
						Performance series for forward-looking scenarios are not available.
					</Banner>
					<div style={{ height: 50 + 48 * clamp(rows.length, 1, 6) }}>
						<AgGridWithModules<ConceptMetricsRow> rowData={rows} columnDefs={columnDefs} />
					</div>
				</Column>
			)}
		</PortfolioQueryWidgetBase>
	);
}
